import * as React from 'react';
import { Brain, RotateCcw, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import { detectTextDirection } from '../../utils/textUtils';
import './BrainShareReviewModal.css';

export interface BrainShareEntry {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  created_at: string;
  project_name: string | null;
  knowledge: string;
  status: string;
  approved_by_supervisor: string | null;
  approved_by_supervisor_at: string | null;
  updated_at: string;
}

type BrainShareTab = 'pending' | 'approved' | 'approved_by_supervisor';

interface BrainShareReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  subProjectId: string;
  onCountsChanged: () => void;
  onNotify?: (banner: { type: 'success' | 'error'; message: string }) => void;
}

export const BrainShareReviewModal: React.FC<BrainShareReviewModalProps> = ({
  isOpen,
  onClose,
  subProjectId,
  onCountsChanged,
  onNotify,
}) => {
  const [allEntries, setAllEntries] = React.useState<BrainShareEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [activeTab, setActiveTab] = React.useState<BrainShareTab>('pending');
  const [approvingIds, setApprovingIds] = React.useState<Set<string>>(new Set());
  const [undoingIds, setUndoingIds] = React.useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = React.useState<Set<string>>(new Set());
  const [entryToDelete, setEntryToDelete] = React.useState<string | null>(null);

  const loadEntries = React.useCallback(async () => {
    if (!subProjectId) return;
    try {
      setLoading(true);
      const response = await apiService.getUserBrainShares(subProjectId);
      if (response.success) {
        setAllEntries((response.data as BrainShareEntry[]) || []);
      } else {
        onNotify?.({ type: 'error', message: response.message || 'Failed to load BrainShare entries' });
      }
    } catch (err) {
      console.error('Error loading BrainShare entries:', err);
      onNotify?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to load BrainShare entries',
      });
    } finally {
      setLoading(false);
    }
  }, [subProjectId, onNotify]);

  React.useEffect(() => {
    if (isOpen && subProjectId) {
      void loadEntries();
    }
  }, [isOpen, subProjectId, loadEntries]);

  const pendingEntries = allEntries.filter((entry) => entry.status === 'in_process');
  const approvedEntries = allEntries.filter((entry) => entry.status === 'approved_by_user');
  const approvedBySupervisorEntries = allEntries.filter(
    (entry) => entry.status === 'approved_by_supervisor' || entry.approved_by_supervisor !== null,
  );

  const entries =
    activeTab === 'pending'
      ? pendingEntries
      : activeTab === 'approved'
        ? approvedEntries
        : approvedBySupervisorEntries;

  const footerLabel =
    activeTab === 'pending'
      ? `${pendingEntries.length} ${pendingEntries.length === 1 ? 'entry' : 'entries'} pending approval`
      : activeTab === 'approved'
        ? `${approvedEntries.length} ${approvedEntries.length === 1 ? 'entry' : 'entries'} approved`
        : `${approvedBySupervisorEntries.length} ${approvedBySupervisorEntries.length === 1 ? 'entry' : 'entries'} approved by supervisor`;

  const handleApprove = async (entryId: string) => {
    setApprovingIds((prev) => new Set(prev).add(entryId));
    try {
      const response = await apiService.updateBrainShareStatus(entryId, 'approved_by_user');
      if (response.success) {
        onNotify?.({ type: 'success', message: 'BrainShare entry approved successfully' });
        setAllEntries((prev) =>
          prev.map((entry) =>
            entry.id === entryId ? { ...entry, status: 'approved_by_user' } : entry,
          ),
        );
        setActiveTab('approved');
        onCountsChanged();
      } else {
        onNotify?.({ type: 'error', message: response.message || 'Failed to approve entry' });
      }
    } catch (err) {
      onNotify?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to approve entry',
      });
    } finally {
      setApprovingIds((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    }
  };

  const handleUndoApproval = async (entryId: string) => {
    setUndoingIds((prev) => new Set(prev).add(entryId));
    try {
      const response = await apiService.updateBrainShareStatus(entryId, 'in_process');
      if (response.success) {
        onNotify?.({ type: 'success', message: 'Approval undone successfully' });
        const nextStatus =
          (response.data as { status?: string } | undefined)?.status || 'in_process';
        setAllEntries((prev) =>
          prev.map((entry) =>
            entry.id === entryId ? { ...entry, status: nextStatus } : entry,
          ),
        );
        setActiveTab('pending');
        onCountsChanged();
      } else {
        onNotify?.({ type: 'error', message: response.message || 'Failed to undo approval' });
      }
    } catch (err) {
      onNotify?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to undo approval',
      });
    } finally {
      setUndoingIds((prev) => {
        const next = new Set(prev);
        next.delete(entryId);
        return next;
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!entryToDelete) return;
    setDeletingIds((prev) => new Set(prev).add(entryToDelete));
    try {
      const response = await apiService.deleteBrainShare(entryToDelete);
      if (response.success) {
        onNotify?.({ type: 'success', message: 'BrainShare entry deleted successfully' });
        setAllEntries((prev) => prev.filter((entry) => entry.id !== entryToDelete));
        onCountsChanged();
        setEntryToDelete(null);
      } else {
        onNotify?.({ type: 'error', message: response.message || 'Failed to delete entry' });
      }
    } catch (err) {
      onNotify?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete entry',
      });
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(entryToDelete);
        return next;
      });
    }
  };

  const isEntryBusy = (entryId: string) =>
    approvingIds.has(entryId) || undoingIds.has(entryId) || deletingIds.has(entryId);

  if (!isOpen) return null;

  return (
    <>
      <div className="cc-modal-overlay cc-modal-overlay--brainshare-review" onClick={onClose}>
        <div
          className="cc-modal cc-modal--brainshare-review"
          role="dialog"
          aria-labelledby="cc-brainshare-review-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="cc-brainshare-review-header">
            <div className="cc-brainshare-review-title-row">
              <Brain className="cc-brainshare-review-icon" aria-hidden />
              <div>
                <h2 className="cc-brainshare-review-title" id="cc-brainshare-review-title">
                  Review BrainShare
                </h2>
                <p className="cc-brainshare-review-subtitle">
                  Manage your shared knowledge with your team
                </p>
              </div>
            </div>
            <button type="button" className="cc-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="cc-brainshare-review-tabs">
            <button
              type="button"
              className={`cc-brainshare-review-tab${activeTab === 'pending' ? ' cc-active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending Approval
              {pendingEntries.length > 0 && (
                <span className="cc-brainshare-review-tab-badge">{pendingEntries.length}</span>
              )}
            </button>
            <button
              type="button"
              className={`cc-brainshare-review-tab${activeTab === 'approved' ? ' cc-active' : ''}`}
              onClick={() => setActiveTab('approved')}
            >
              Approved
              {approvedEntries.length > 0 && (
                <span className="cc-brainshare-review-tab-badge cc-green">{approvedEntries.length}</span>
              )}
            </button>
            <button
              type="button"
              className={`cc-brainshare-review-tab${activeTab === 'approved_by_supervisor' ? ' cc-active' : ''}`}
              onClick={() => setActiveTab('approved_by_supervisor')}
            >
              Approved by Supervisor
              {approvedBySupervisorEntries.length > 0 && (
                <span className="cc-brainshare-review-tab-badge cc-purple">
                  {approvedBySupervisorEntries.length}
                </span>
              )}
            </button>
          </div>

          <div className="cc-brainshare-review-body">
            {loading ? (
              <div className="cc-brainshare-review-loading">
                <span className="cc-action-banner-spinner" aria-hidden />
                <span>Loading entries…</span>
              </div>
            ) : entries.length === 0 ? (
              <div className="cc-brainshare-review-empty">
                <Brain className="cc-brainshare-review-empty-icon" aria-hidden />
                <p>
                  {activeTab === 'pending'
                    ? 'No pending entries'
                    : activeTab === 'approved'
                      ? 'No approved entries'
                      : 'No supervisor-approved entries'}
                </p>
              </div>
            ) : (
              <div className="cc-brainshare-review-list">
                {entries.map((entry) => {
                  const busy = isEntryBusy(entry.id);
                  const isSupervisorApproved =
                    entry.status === 'approved_by_supervisor' || entry.approved_by_supervisor !== null;
                  const isPending = entry.status === 'in_process';
                  const isUserApproved = entry.status === 'approved_by_user';

                  return (
                    <div key={entry.id} className="cc-brainshare-review-entry">
                      <div className="cc-brainshare-review-entry-top">
                        <div className="cc-brainshare-review-entry-info">
                          <div className="cc-brainshare-review-entry-user">
                            {entry.user_name || 'Unknown User'}
                          </div>
                          {entry.user_email && (
                            <div className="cc-brainshare-review-entry-email">{entry.user_email}</div>
                          )}
                          <div className="cc-brainshare-review-entry-meta">
                            <span>{new Date(entry.created_at).toLocaleDateString()}</span>
                            {entry.project_name && (
                              <span>Project: {entry.project_name}</span>
                            )}
                          </div>
                        </div>

                        <div className="cc-brainshare-review-entry-actions">
                          {isPending && (
                            <button
                              type="button"
                              className="cc-brainshare-btn cc-brainshare-btn--approve"
                              disabled={busy}
                              onClick={() => void handleApprove(entry.id)}
                            >
                              {approvingIds.has(entry.id) ? 'Approving…' : 'Approve'}
                            </button>
                          )}
                          {isSupervisorApproved && (
                            <span className="cc-brainshare-status-badge cc-brainshare-status-badge--supervisor">
                              Approved by Supervisor
                            </span>
                          )}
                          {isUserApproved && (
                            <div className="cc-brainshare-approved-actions">
                              <span className="cc-brainshare-status-badge cc-brainshare-status-badge--approved">
                                Approved
                              </span>
                              <button
                                type="button"
                                className="cc-brainshare-btn cc-brainshare-btn--undo"
                                disabled={busy}
                                onClick={() => void handleUndoApproval(entry.id)}
                              >
                                <RotateCcw className="cc-brainshare-btn-icon" aria-hidden />
                                {undoingIds.has(entry.id) ? 'Undoing…' : 'Undo Approval'}
                              </button>
                            </div>
                          )}
                          {!isSupervisorApproved && (
                            <button
                              type="button"
                              className="cc-brainshare-btn cc-brainshare-btn--delete"
                              disabled={busy}
                              onClick={() => setEntryToDelete(entry.id)}
                            >
                              <Trash2 className="cc-brainshare-btn-icon" aria-hidden />
                              {deletingIds.has(entry.id) ? 'Deleting…' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="cc-brainshare-review-knowledge">
                        <div className="cc-brainshare-review-knowledge-label">Knowledge:</div>
                        <p
                          className="cc-brainshare-review-entry-text"
                          dir={detectTextDirection(entry.knowledge)}
                        >
                          {entry.knowledge}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="cc-brainshare-review-footer">
            <span className="cc-brainshare-review-footer-count">{footerLabel}</span>
            <button type="button" className="cc-btn cc-btn-cancel" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      {entryToDelete && (
        <div
          className="cc-modal-overlay cc-modal-overlay--brainshare-delete"
          onClick={() => !deletingIds.has(entryToDelete) && setEntryToDelete(null)}
        >
          <div
            className="cc-brainshare-delete-modal"
            role="dialog"
            aria-labelledby="cc-brainshare-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cc-brainshare-delete-header" id="cc-brainshare-delete-title">
              Delete BrainShare Entry?
            </div>
            <div className="cc-brainshare-delete-body">
              <p className="cc-brainshare-delete-message">
                Are you sure you want to delete this BrainShare entry? This action cannot be undone.
              </p>
              <div className="cc-brainshare-delete-actions">
                <button
                  type="button"
                  className="cc-brainshare-btn cc-brainshare-btn--undo"
                  disabled={deletingIds.has(entryToDelete)}
                  onClick={() => setEntryToDelete(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cc-brainshare-btn cc-brainshare-btn--delete"
                  disabled={deletingIds.has(entryToDelete)}
                  onClick={() => void handleDeleteConfirm()}
                >
                  {deletingIds.has(entryToDelete) ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
